import {  useEffect } from 'preact/hooks';
import {createRef} from "preact";
import { observable } from 'mobx';
import { autobind } from 'core-decorators';
import captchaDecoUtil from '@src/utils/captcha-deco-util';
import JsonService from '@src/services/json';
import _ from 'lodash';
import intl from 'react-intl-universal';
// import Toast from '@src/components/toast';
import message from '@src/utils/message';
import lockAsync from '@src/utils/lock-async';
import useRequest from '@src/utils/hooks/use-request';
import rsa from '@xfe/rsa-zt';
import accountStore from '@src/models/account';
// import SDK from '@src/utils/bridge/xi-gua-sdk';
import { $$tracker } from '@src/tracker';
import desensitizationEmail from '@src/utils/desensitizationEmail';

export default class RegisterStore {
  formRef = createRef();
  /**
   * 用户表单
   * @type {{}}
   */
  @observable formValue = {};

  /**
   * 游戏内模式
   * @type {boolean}
   */
  inGameMode = false;

  /**
   * 协议状态
   * @type {boolean}
   */
  @observable protocolStatus = false;

  $service = JsonService.getZTDefaultInstance({});

  /**
   * 提交按钮禁止状态
   * @type {boolean}
   */
  @observable submitBtnDisable = true;

  @observable loadingStatus = false;

  /**
   * 验证码置灰
   * @type {boolean}
   */
  @observable sendCodeDisable = true;

  @observable
  link = {
    userAgreement: '',
    privacyAgreement: ''
  };

  publicKey = '';

  /**
   * 发送验证码表单验证
   */
  @autobind
  async beforeSendCode() {
    const checkEmail = new Promise((resolve, reject) => {
      this.formRef.current.props.form.validateFields(['email'], error => {
        if (!error) {
          resolve();
        } else {
          reject(error);
        }
      });
    });
    await checkEmail;
    return await this.sendCode();
  }

  /**
   * 发送验证码
   * @param [params]
   * @return {Promise<void>}
   */
  @autobind
  @captchaDecoUtil('zt')
  async sendCode(params) {
    try {
      await this.$service.sendCaptcha({
        email: this.formRef.current.props.form.getFieldValue('email'),
        type: 'register',
        ext: JSON.stringify(params)
      });
      message.success(intl.get('EMAIL.SEND_CODE.SUCCESS.TEXT', { email: this.formRef.current.props.form.getFieldValue('email') }));
    } catch (e) {
      message.error(e.message);
      throw new Error(e.message);
    }
  }

  checkEmail(email) {
    this.sendCodeDisable = !email;
  }

  checkCodeValue(value) {
    let check = true;
    Object.keys(value).forEach(key => {
      if (!_.trim(value[key])) {
        check = false;
      }
    });
    if (check) {
      this.submitBtnDisable = false;
    } else {
      this.submitBtnDisable = true;
    }
  }

  @autobind
  changeProtocolStatus(value) {
    this.protocolStatus = value;
  }

  debounceMark = false;

  /**
   * 注册账号
   * @return {Promise<void>}
   */
  @autobind
  @lockAsync()
  async registerHandle() {
    if (this.debounceMark) {
      return;
    }
    this.debounceMark = true;
    setTimeout(() => {
      this.debounceMark = false;
    }, 200);
    if (!this.protocolStatus) {
      message.error(intl.get('PAGE.REGISTER.PROTOCOL.REQUIRED.TEXT'));
      return;
    }
    const asyncFormValid = new Promise((resolve, reject) => {
      this.formRef.current.props.form.validateFieldsAndScroll((error, value) => {
        if (!error) {
          resolve(value);
        } else {
          reject(new Error(JSON.stringify(error)));
        }
      });
    });
    $$tracker.registerAccountStart(desensitizationEmail(this.formRef.current.props.form.getFieldValue('email')));
    try {
      const formValue = await asyncFormValid;
      this.formValue = Object.keys(formValue).reduce((current, key) => {
        current[key] = _.trim(formValue[key]);
        return { ...current };
      }, {});
      const data = await this.$service.register({
        email: this.formValue.email,
        captcha: this.formValue.code,
        password: rsa.encrypt(this.formValue.password)
      });
      await this.$service.setLoginInfo({ token: data.token });
      $$tracker.registerAccountSuccess(desensitizationEmail(this.formValue.email), accountStore.openId);
      accountStore.loginType = 'register';
      accountStore.updateStatus(data)
      message.success(intl.get('PAGE.REGISTER.SUCCESS.TEXT', { email: this.formValue.email }));

      // await SDK.RegisterSuccess({ uid: accountStore.uid, token: accountStore.token });
    } catch (e) {
      if (e) {
        let code = e.code;
        if (e.serviceResponse) {
          code = e.serviceResponse.code;
        }
        $$tracker.registerAccountFail(desensitizationEmail(this.formRef.current.props.form.getFieldValue('email')), e.message, code);
        if (typeof e.code === 'number') {
          message.error(e.message);
        }
      }
    }
  }

  use() {
    const getConfigRequest = useRequest(() => this.$service.getConfig({}));
    useEffect(
      () => {
        this.loadingStatus = getConfigRequest.loading;
      },
      [getConfigRequest.loading]
    );
    useEffect(
      () => {
        if (getConfigRequest.error) {
          message.error(getConfigRequest.error.message);
        }
      },
      [getConfigRequest.error]
    );
    useEffect(
      () => {
        if (getConfigRequest.data) {
          const { privacyAgreement, userAgreement,platformPrivacyAgreement, platformUserAgreement, accountPublicKey } = getConfigRequest.data;
          this.link.privacyAgreement = platformPrivacyAgreement;
          this.link.userAgreement = platformUserAgreement;
          rsa.updatePublicKey(accountPublicKey);
          this.publicKey = accountPublicKey;
          if (this.inGameMode) {
            setTimeout(() => {
              for (let i in document.querySelectorAll('.link')) {
                const element = document.querySelectorAll('.link')[i];
                if (typeof element !== 'object') {
                  return;
                }
                const href = element.href;
                element.target = '_self';
                element.href = '';
                element.addEventListener('click', e => {
                  e.stopPropagation();
                  e.preventDefault();
                  // DK.OpenSystemBrowser({ url: href }).finally();
                });
              }
              /*document.querySelectorAll('.link').forEach(element => {
                const href = element.href;
                element.target = '_self';
                element.href = '';
                element.addEventListener('click', e => {
                  e.stopPropagation();
                  e.preventDefault();
                  SDK.OpenSystemBrowser({ url: href }).finally();
                });*/
              //});
            });
          }
        }
      },
      [getConfigRequest.data]
    );
  }
}
